import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { FileText, CheckCircle2, Clock, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CONTRACT_TEXT = `# УМОВИ СПІВПРАЦІ З 3D ХУДОЖНИКОМ

Цей документ визначає стандартні умови співпраці між компанією **Smartorange** та 3D художником (далі Виконавець) у форматі підрядних робіт.

## 1. Формат співпраці

1.1. Smartorange формує перелік завдань по проєкту з чітким описом обсягу робіт, дедлайнів та вартості кожного завдання.
1.2. Виконавець має право прийняти завдання або відмовитись від нього без пояснення причин.
1.3. У разі прийняття завдання Виконавець зобов'язується виконати його у повному обсязі та в узгоджені строки.

## 2. Вартість робіт

2.1. Вартість кожного завдання визначається **Smartorange** виходячи з бюджету та маржинальності проєкту.
2.2. Фіксованого прайсу в договорі не встановлюється.
2.3. Погодження вартості відбувається шляхом прийняття Виконавцем конкретного завдання в системі управління проєктами.

## 3. Терміни виконання

3.1. Для кожного завдання Smartorange встановлює дедлайн, який фіксується в системі управління проєктами.
3.2. Прийняття завдання означає повну згоду Виконавця з дедлайном.
3.3. Неможливість виконання завдання в строк повинна бути повідомлена **до моменту прийняття завдання**.

## 4. Прострочення та відповідальність

4.1. У разі прострочення дедлайну без попереднього погодження з Smartorange застосовується пеня.
4.2. Розмір пені становить **1 відсоток від вартості завдання за кожен календарний день прострочення**, але не більше **30 відсотків** від загальної вартості завдання.
4.3. У разі критичного прострочення Smartorange має право зняти завдання з Виконавця з оплатою лише фактично прийнятого обсягу робіт.

## 5. Пріоритет та завантаженість

5.1. Виконавець зобов'язується не брати в роботу обсяг завдань, який унеможливлює своєчасну здачу прийнятих проєктів.
5.2. Прийняття завдання підтверджує, що Виконавець має достатній ресурс для його виконання.

## 6. Правки та обсяг робіт

6.1. У вартість завдання входить до **3 турів правок** або еквівалент погодженої кількості робочих годин.
6.2. Додаткові роботи, що виходять за межі початкового ТЗ, оплачуються окремо за погодженням сторін.

## 7. Передача матеріалів

7.1. Виконавець зобов'язується передати фінальні рендери, а також робочі сцени у форматі, визначеному Smartorange.
7.2. До моменту повної оплати Smartorange має право вимагати використання водяних знаків на прев'ю матеріалах.

## 8. Оплата

8.1. Оплата здійснюється **після прийняття результату робіт** зі сторони Smartorange або кінцевого клієнта.
8.2. Максимальний термін оплати становить **до 17 календарних днів** з моменту прийняття робіт.
8.3. Виконавець підтверджує, що погоджується з відстрочкою платежу та не має претензій щодо цього.

## 9. Комунікація та контроль

9.1. Вся робота ведеться виключно через систему управління завданнями, визначену Smartorange.
9.2. Виконавець зобов'язується бути на зв'язку у робочі дні з **10:00 до 17:00** за київським часом.

## 10. Завершальні положення

10.1. Ці умови є універсальними та застосовуються до всіх проєктів Smartorange.
10.2. Прийняття першого завдання вважається автоматичною згодою Виконавця з цими умовами.
10.3. Smartorange залишає за собою право оновлювати умови з попереднім повідомленням.`;

const ArtistContractPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [contractStatus, setContractStatus] = useState({ accepted: false, accepted_at: null });
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    checkContractStatus();
  }, []);

  const checkContractStatus = async () => {
    try {
      const res = await api.get("/artist/contract-status");
      setContractStatus(res.data);
    } catch (error) {
      console.error("Error checking contract:", error);
    } finally {
      setLoading(false);
    }
  };

  const acceptContract = async () => {
    if (!agreed) {
      toast.error("Please confirm that you have read and agree to the terms");
      return;
    }

    setAccepting(true);
    try {
      await api.post("/artist/accept-contract");
      toast.success("Contract accepted successfully!");
      setContractStatus({ accepted: true, accepted_at: new Date().toISOString() });
      // Redirect to tasks after a short delay
      setTimeout(() => navigate("/artist-dashboard"), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept contract");
    } finally {
      setAccepting(false);
    }
  };

  // Render markdown-like formatting
  const renderContract = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-2xl font-bold text-zinc-50 mt-6 mb-4">{line.slice(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-lg font-semibold text-orange-400 mt-6 mb-3">{line.slice(3)}</h2>;
      }
      if (line.match(/^\d+\.\d+\./)) {
        // Replace **text** with bold
        const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-zinc-50">$1</strong>');
        return (
          <p key={i} className="text-zinc-300 mb-2 pl-4" dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      }
      if (line.trim() === '') {
        return <div key={i} className="h-2" />;
      }
      return <p key={i} className="text-zinc-400 mb-2">{line}</p>;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="artist-contract-page" className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
            <FileText className="h-7 w-7 text-orange-500" />
            Collaboration Agreement
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Please read and accept the terms before starting work
          </p>
        </div>
        
        {contractStatus.accepted && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-900/30 border border-green-700 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-green-400" />
            <div>
              <p className="text-sm font-medium text-green-400">Contract Accepted</p>
              <p className="text-xs text-zinc-500">
                {new Date(contractStatus.accepted_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Contract Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-4 border-b border-zinc-800">
          <CardTitle className="text-zinc-50 flex items-center gap-2">
            <FileText className="h-5 w-5 text-orange-500" />
            Terms & Conditions
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="max-h-[500px] overflow-y-auto pr-4 custom-scrollbar">
            {renderContract(CONTRACT_TEXT)}
          </div>
        </CardContent>
      </Card>

      {/* Accept Section */}
      {!contractStatus.accepted && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3 mb-6">
              <Checkbox
                id="agree"
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-1 border-zinc-600 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
              />
              <label htmlFor="agree" className="text-sm text-zinc-300 cursor-pointer">
                I have read and fully understand the collaboration terms. I agree to comply with all 
                conditions stated in this document and accept my responsibilities as an Artist working 
                with Smartorange.
              </label>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={acceptContract}
                disabled={!agreed || accepting}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {accepting ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Accept Contract
                  </>
                )}
              </Button>
              
              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                onClick={() => {
                  // Create downloadable text file
                  const blob = new Blob([CONTRACT_TEXT], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'Smartorange_Artist_Contract.txt';
                  a.click();
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Already accepted - show button to go to tasks */}
      {contractStatus.accepted && (
        <div className="flex justify-center">
          <Button
            onClick={() => navigate("/artist-dashboard")}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            Go to My Dashboard
          </Button>
        </div>
      )}
    </div>
  );
};

export default ArtistContractPage;
